<script setup>
import { useCartStore } from '@/stores/Cart'
import { useDisplay } from 'vuetify'

const { smAndDown, smAndUp } = useDisplay()
const nuxtApp = useNuxtApp()
const cart = useCartStore()
const route = useRoute()
const staticURLImg = useRuntimeConfig().public.staticURLImg
const { formatUSD } = useFormatters()

const { data, status, error } = await useLazyAsyncData(
    `product-${route.params.slug}`,
    () => $fetch(`/api/product/${route.params.slug}`),
    {
        transform: (data) => {
            return {
                product: data.products.edges[0].node,
                fetchedAt: new Date(Date.now()),
            }
        },
        getCachedData(key) {
            const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key]

            if (!data) {
                return
            }

            const expirationDate = new Date(data.fetchedAt)
            expirationDate.setTime(expirationDate.getTime() + 60 * 60000) // cache set to 1 hour
            const isExpired = expirationDate.getTime() < Date.now()
            if (isExpired) {
                return
            }

            return data
        },
    }
)

watchEffect(() => {
    if (error.value) {
        throw createError({
            statusCode: 500,
            message: error.value,
            fatal: false,
        })
    }
})

// Calculate currentPrice of a product
const currentPrice = computed(() => {
    if (data.value && data.value?.product?.productPrices?.totalCount > 0) {
        return data.value?.product?.productPrices?.edges[0]?.node?.price
    } else {
        return data.value?.product?.price
    }
})

// These are for SEO meta
const title = computed(() => {
    // console.log('title computed = ', data.value?.product?.name + ' - ' + data.value?.product?.tagline)
    return data.value ? data.value?.product?.name + ' - ' + data.value?.product?.tagline : ''
})

const desc = computed(() => {
    if (data.value) {
        const paragraphs = data.value?.product?.description.split('</p>')
        const firstParagraph = paragraphs[0].replace(/<(.|\n)*?>/g, '')
        return firstParagraph ? firstParagraph : ''
    }
})

const ogImg = computed(() => {
    return data.value ? staticURLImg + data.value?.product?.productImages?.edges[1]?.node.source : ''
})

useSeoMeta({
    title: title,
    ogTitle: title,
    description: desc,
    ogDescription: desc,
    ogImage: ogImg,
    twitterCard: 'summary_large_image',
})
</script>

<template>
    <v-row id="productdetail" class="mt-1 justify-center">
        <v-col v-if="status === 'pending' || status === 'idle'" cols="12" sm="10"><app-loader /></v-col>
        <!-- <v-col>{{ data }}</v-col> -->
        <v-col v-else-if="data.product" cols="12">
            <v-row class="slide-from-top justify-center">
                <v-col cols="12" md="6">
                    <v-card variant="flat">
                        <v-img
                            id="cardImg"
                            :src="staticURLImg + data.product.productImages.edges[0]?.node.source"
                            :alt="data.product.name"
                        >
                            <v-container
                                v-if="currentPrice > 0 && currentPrice != data.product.price"
                                class="pa-2 d-flex justify-start text-grey-lighten-3"
                            >
                                <span class="pl-2 pr-2 bg-secondary rounded">
                                    {{ data.product.productPrices.edges[0].node.description }}
                                </span>
                            </v-container>
                        </v-img>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4" :class="smAndDown ? `text-center` : null">
                    <v-row no-gutters class="d-flex flex-column">
                        <v-col>
                            <v-chip density="compact" label>
                                {{ data.product.category.name }}
                            </v-chip>
                        </v-col>
                        <v-col class="pt-2 text-h4 font-weight-medium">{{ data.product.name }}</v-col>
                        <v-col class="text-subtitle-2 font-weight-regular"> {{ data.product.tagline }} </v-col>
                        <v-col class="pt-2">
                            <app-link
                                :to="`/seller/${data.product.seller.handle}`"
                                :lnk-text="data.product.seller.displayName"
                                :aria-label="`Visit ${data.product.seller.displayName} profile`"
                            />
                        </v-col>
                        <v-col class="py-6 text-h6 font-weight-bold">
                            <v-row no-gutters class="d-flex flex-row" :class="smAndDown ? `justify-center` : null">
                                <v-col
                                    cols="2"
                                    :class="currentPrice != data.product.price ? 'text-decoration-line-through' : null"
                                    style="min-width: fit-content"
                                >
                                    {{ formatUSD(data.product.price) }}
                                </v-col>
                                <v-col v-if="currentPrice > 0 && currentPrice != data.product.price">
                                    <span class="px-2 bg-secondary rounded-pill">{{ formatUSD(currentPrice) }}</span>
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col class="pt-md-2">
                            <v-row class="flex-column">
                                <v-col>
                                    <app-button
                                        id="btnaddtocart"
                                        :btn-text="`Add to Cart`"
                                        :show-tooltip="true"
                                        :tooltip-text="`Add to Cart`"
                                        :prepend-icon="`mdi-cart-plus`"
                                        @click.stop="cart.addProduct(data.product)"
                                        :data-prodname="data.product.name"
                                        aria-label="Add product to shopping cart"
                                    />
                                </v-col>
                                <v-col v-if="data.product.productProp.demo">
                                    <app-button
                                        id="btndownloaddemo"
                                        variant="outlined"
                                        :btn-text="`Demo`"
                                        :show-tooltip="true"
                                        :tooltip-text="`Download Demo`"
                                        :prepend-icon="`mdi-download`"
                                        :href="`${useRuntimeConfig().public.demoURL}?token=${
                                            data.product.productProp.dlToken
                                        }`"
                                        target="_blank"
                                        :data-prodname="data.product.name"
                                        aria-label="Download product demo"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                        <v-col class="d-flex pt-6 justify-center justify-md-start align-center">
                            <span class="mr-2 font-weight-medium">Share:</span>
                            <social-share
                                v-for="network in [
                                    'facebook',
                                    'x',
                                    'reddit',
                                    'pinterest',
                                    'threads',
                                    'skype',
                                    'whatsapp',
                                    'email',
                                ]"
                                :key="network"
                                :network="network"
                                :styled="true"
                                :label="false"
                                :title="title"
                                :image="staticURLImg + data.product.productImages.edges[0]?.node.source"
                                class="ma-1 pa-1 text-grey"
                            >
                                <!-- <template #icon> <v-icon size="32" :icon="`mdi-${network}`"></v-icon></template> -->
                            </social-share>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="11" sm="10" class="st-text-normal text-justify">
                    <div v-dompurify-html="data.product.description"></div>
                </v-col>
            </v-row>
            <v-row v-if="data.product.productProp.containAds" class="justify-center">
                <v-col cols="7" class="py-0 text-center">
                    <lazy-ad-body spot="center" :page="data.product.handle" position="center" class="mb-10" />
                </v-col>
            </v-row>
            <v-lazy v-if="data.product.productFeatures.totalCount > 0" :options="{ threshold: 0.5 }">
                <lazy-product-features
                    :id="data.product.id"
                    :handle="data.product.handle"
                    class="bg-grey-darken-4 slide-from-left"
                />
            </v-lazy>
            <v-lazy v-if="data.product.productVideos.totalCount > 0" :options="{ threshold: 0.5 }">
                <lazy-product-videos :id="data.product.id" :handle="data.product.handle" class="slide-from-right" />
            </v-lazy>
            <v-lazy v-if="data.product.productAudio.totalCount > 0" :options="{ threshold: 0.5 }">
                <lazy-product-audios :id="data.product.id" :handle="data.product.handle" class="slide-from-left" />
            </v-lazy>
            <v-lazy v-if="data.product.totalImages.totalCount > 0" :options="{ threshold: 0.5 }">
                <lazy-product-images
                    :id="data.product.id"
                    :handle="data.product.handle"
                    class="text-grey-lighten-4 slide-from-right"
                    style="background-color: #161616 !important"
                />
            </v-lazy>
            <v-lazy :options="{ threshold: 0.5 }">
                <lazy-products-seller
                    :seller="data.product.seller.displayName"
                    :handle="data.product.seller.handle"
                    class="bg-grey-darken-4 slide-from-left"
                />
            </v-lazy>
            <v-lazy v-if="data.product.similar.totalCount > 0" :options="{ threshold: 0.5 }">
                <lazy-products-similar :id="data.product.id" :handle="data.product.handle" class="slide-from-right" />
            </v-lazy>
            <v-lazy v-if="data.product.productVersions.totalCount > 0" :options="{ threshold: 0.5 }">
                <lazy-product-roadmap
                    :id="data.product.id"
                    :handle="data.product.handle"
                    class="bg-grey-darken-4 slide-from-left"
                />
            </v-lazy>
            <v-lazy v-if="data.product.productProp.containAds" class="justify-center">
                <lazy-ad-disclosure class="mt-4 fade-in-up" />
            </v-lazy>
        </v-col>
    </v-row>
</template>

<style scoped>
.right-chevron {
    -webkit-clip-path: polygon(90% 0, 100% 50%, 90% 100%, 0 100%, 10% 50%, 0 0);
    clip-path: polygon(90% 0, 100% 50%, 90% 100%, 0 100%, 10% 50%, 0 0);
    background-color: #e0e0e0 !important;
    text-transform: uppercase !important;
    font-weight: 500 !important;
    font-size: small !important;
}
</style>
